import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Download, FlaskConical, Search, Users } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const LabBlock = ({ lab, q }) => {
  const rows = lab.registrants.filter(r => !q || `${r.name} ${r.job_title} ${r.company}`.toLowerCase().includes(q));
  const pct = lab.max ? Math.min(100, Math.round((lab.count / lab.max) * 100)) : 0;
  return (
    <div className="border border-slate-200 rounded-lg overflow-hidden" data-testid={`britelab-report-lab-${lab.item_id}`}>
      <div className="bg-slate-50 px-4 py-3 flex flex-wrap items-center gap-x-4 gap-y-1">
        <p className="font-semibold text-sm text-slate-800 flex-1 min-w-[200px]">{lab.title}</p>
        <span className="text-xs text-slate-500">{lab.time}{lab.room ? ` · ${lab.room}` : ''}</span>
        <span className="text-xs font-semibold text-brand-navy" data-testid={`britelab-report-count-${lab.item_id}`}>
          {lab.count}{lab.max ? ` / ${lab.max}` : ''} registered
        </span>
        {lab.max && (
          <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${lab.left === 0 ? 'bg-red-100 text-red-700' : 'bg-emerald-100 text-emerald-700'}`}
            data-testid={`britelab-report-left-${lab.item_id}`}>
            {lab.left === 0 ? 'FULL' : `${lab.left} left`}
          </span>
        )}
      </div>
      {lab.max && <div className="h-1 bg-slate-100"><div className="h-1 bg-brand-sage" style={{ width: `${pct}%` }} /></div>}
      {rows.length === 0 ? (
        <p className="px-4 py-3 text-xs text-slate-400 italic">{lab.count ? 'No matches' : 'No registrations yet'}</p>
      ) : (
        <table className="w-full text-xs">
          <thead><tr className="text-left text-slate-400 uppercase tracking-wider text-[10px]">
            <th className="px-4 py-2 font-semibold">Member</th><th className="px-4 py-2 font-semibold">Job title</th><th className="px-4 py-2 font-semibold">Company</th>
          </tr></thead>
          <tbody>
            {rows.map(r => (
              <tr key={r.user_id} className="border-t border-slate-100" data-testid={`britelab-report-row-${lab.item_id}-${r.user_id}`}>
                <td className="px-4 py-2 font-medium text-slate-800">{r.name}</td>
                <td className="px-4 py-2 text-slate-600">{r.job_title || '—'}</td>
                <td className="px-4 py-2 text-slate-600">{r.company || '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
};

export default function BriteLabReportDialog({ event, onClose }) {
  const [data, setData] = useState(null);
  const [q, setQ] = useState('');
  useEffect(() => {
    if (!event) return;
    setData(null); setQ('');
    fetch(`${API}/api/admin/events/${event.id}/britelabs/report`, { credentials: 'include' })
      .then(r => (r.ok ? r.json() : Promise.reject())).then(setData)
      .catch(() => toast.error('Could not load BriteLab registrations'));
  }, [event]);

  const download = async () => {
    const res = await fetch(`${API}/api/admin/events/${event.id}/britelabs/report?format=csv`, { credentials: 'include' });
    if (!res.ok) { toast.error('Download failed'); return; }
    const url = URL.createObjectURL(await res.blob());
    const a = document.createElement('a');
    a.href = url; a.download = `BriteLabs_${(event.title || 'event').replace(/[^a-z0-9]+/gi, '_')}.csv`;
    a.click(); URL.revokeObjectURL(url);
  };

  const labs = data?.britelabs || [];
  const total = labs.reduce((n, l) => n + l.count, 0);
  return (
    <Dialog open={!!event} onOpenChange={o => !o && onClose()}>
      <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto" data-testid="britelab-report-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2"><FlaskConical className="h-5 w-5 text-purple-500" /> BriteLab registrations · {event?.title}</DialogTitle>
        </DialogHeader>
        <div className="flex flex-wrap items-center gap-3">
          <span className="text-sm text-slate-600 inline-flex items-center gap-1.5" data-testid="britelab-report-total">
            <Users className="h-4 w-4" /> {labs.length} BriteLabs · {total} registrations
          </span>
          <div className="relative flex-1 min-w-[180px]">
            <Search className="h-3.5 w-3.5 absolute left-2.5 top-2.5 text-slate-400" />
            <Input value={q} onChange={e => setQ(e.target.value)} placeholder="Search name, job title or company" className="h-8 pl-8 text-xs" data-testid="britelab-report-search" />
          </div>
          <Button size="sm" onClick={download} disabled={!labs.length} className="bg-brand-navy hover:bg-brand-dark text-white" data-testid="britelab-report-download">
            <Download className="h-4 w-4 mr-1.5" /> Download (Excel/CSV)
          </Button>
        </div>
        <div className="space-y-3 mt-2">
          {!data && <p className="text-sm text-slate-400">Loading…</p>}
          {data && labs.length === 0 && <p className="text-sm text-slate-400 italic">This event has no BriteLab sessions.</p>}
          {labs.map(l => <LabBlock key={l.item_id} lab={l} q={q.trim().toLowerCase()} />)}
        </div>
      </DialogContent>
    </Dialog>
  );
}
